class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

class ClearCart extends HTMLElement {
  constructor() {
    super();

    async function clearCart() {
      console.log('Clearing cart');
      await fetch(window.Shopify.routes.root + 'cart/clear.js', { method: 'POST' })
        .then(() => {
          console.log('Your cart has been cleared!');
          location.reload();
        })
        .catch((error) => {
          console.error('Error clearing cart:', error);
        });
    }

    this.addEventListener('click', (event) => {
      clearCart();
    });
  }
}

customElements.define('clear-cart', ClearCart);

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.instanceId = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    this.lineItemStatusElement = document.getElementById('CartDrawer-LineItemStatus');
    this.setupQuantitySelectors();
    this.setupCartUpdateListener();
  }

  // Helper function to update sections with new markup
  updateSectionsWithMarkup(sectionsData) {
    console.log('[CartItems] Updating sections with markup:', sectionsData);

    Object.entries(sectionsData).forEach(([sectionId, html]) => {
      // Handle both section ID formats
      const elementToReplace =
        document.getElementById(sectionId) || document.getElementById(`shopify-section-${sectionId}`);

      if (!elementToReplace) {
        console.warn(`[CartItems] Could not find element for section: ${sectionId}`);
        return;
      }

      // Parse the HTML to find the specific element we want to update
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get the sections to render configuration
      const sectionsToRender = this.getSectionsToRender();
      const sectionConfig = sectionsToRender.find((section) => section.id === sectionId);

      if (sectionConfig && sectionConfig.selector) {
        // Find the specific element in the new HTML
        const newContent = doc.querySelector(sectionConfig.selector);
        if (!newContent) return;
        // Find the corresponding element in the current DOM
        const targetElement = elementToReplace.querySelector(sectionConfig.selector);
        if (!targetElement) return;

        // GUARD: if the incoming HTML contains a <cart-drawer-items> and we're currently in one,
        // only replace *this* component's inner content to avoid nesting new instances.
        const incomingDrawerItems = newContent.querySelector('cart-drawer-items');
        const thisIsCartDrawerItems = this.tagName === 'CART-DRAWER-ITEMS';
        if (thisIsCartDrawerItems && incomingDrawerItems) {
          console.log('[CartItems] Safe-replacing inner HTML of current <cart-drawer-items>');
          this.innerHTML = incomingDrawerItems.innerHTML;
        } else {
          console.log(`[CartItems] Updating section ${sectionId} with selector ${sectionConfig.selector}`);
          targetElement.innerHTML = newContent.innerHTML;
        }
      } else {
        console.log(`[CartItems] Updating entire section ${sectionId}`);
        elementToReplace.innerHTML = html;

        if (sectionId === 'cart-icon-bubble') {
          console.log('[CartItems] Updated cart-icon-bubble section, checking cart count');
          const cartCount = elementToReplace.querySelector('.cart-count-bubble span');
          if (cartCount) {
            console.log('[CartItems] Cart count in updated section:', cartCount.textContent);
          }
        }
      }
    });

    console.log('[CartItems] Reinitializing quantity selectors after markup update');
    this.setupQuantitySelectors();
  }

  setupQuantitySelectors() {
    const quantitySelectors = this.querySelectorAll('.cart-quantity__select');
    console.log('[CartItems] Setting up quantity selectors:', {
      count: quantitySelectors.length,
      selectors: Array.from(quantitySelectors).map((s) => ({
        index: s.dataset.index,
        variantId: s.dataset.quantityVariantId,
        currentValue: s.value,
      })),
    });

    // Remove existing event listeners before adding new ones
    quantitySelectors.forEach((selector) => {
      const newSelector = selector.cloneNode(true);
      selector.parentNode.replaceChild(newSelector, selector);
      newSelector.addEventListener('change', this.handleQuantityChange.bind(this));
    });
  }

  setupCartUpdateListener() {
    // Subscribe to cart updates to check for three months free qualification
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      console.log('[CartItems] Cart update event received:', event);
      if (event?.sourceId && event.sourceId === this.instanceId) {
        return;
      }
      if (event.source === 'cart-items' || event.source === 'product-form') {
        // If the update came from a product form, we need to update the cart UI
        if (event.source === 'product-form') {
          const sectionsResponse = await fetch(
            window.Shopify.routes.root +
              '?sections=' +
              this.getSectionsToRender()
                .map((section) => section.id)
                .join(','),
          );
          const sectionsData = await sectionsResponse.json();
          console.log('[CartItems] Sections data received:', sectionsData);

          const cartData = await this.getCartState();
          this.updateSectionsWithMarkup(sectionsData);
          this.refreshCartDrawer();
          this.updateCartUI(cartData);
        }

        // Check for three months free qualification
        await this.doesUserQualifyForThreeMonthsFree();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleQuantityChange(event) {
    const select = event.target;
    const line = select.dataset.index;
    const quantity = parseInt(select.value);
    const name = select.getAttribute('name');
    const variantId = select.dataset.quantityVariantId;

    this.updateQuantity(line, quantity, name, variantId);
  }

  async updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    try {
      const sections = this.getSectionsToRender()
        .map((section) => section.id)
        .join(',');

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          line,
          quantity,
          sections_url: window.location.pathname,
          sections: sections,
        }),
      });

      const cartData = await response.json();

      if (cartData.errors) {
        this.handleError(line, cartData.errors);
        return;
      }

      if (cartData.item_count === 0) {
        this.hideCartUI();
      }

      // Get fresh sections data
      const sectionsResponse = await fetch(
        window.Shopify.routes.root +
          '?sections=' +
          this.getSectionsToRender()
            .map((section) => section.id)
            .join(','),
      );

      const sectionsData = await sectionsResponse.json();

      // Update UI in a single pass
      this.updateSectionsWithMarkup(sectionsData);
      this.updateCartUI(cartData);

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        sourceId: this.instanceId,
        cartData,
      });
    } catch (error) {
      console.error('Error updating cart:', error);
      this.updateLiveRegions(line, error);
    } finally {
      this.disableLoading(line);
    }
  }

  async doesUserQualifyForThreeMonthsFree() {
    const cart = await this.getCartState();
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const doesAnyCartItemHaveTag = cart.items.some((item) => item.properties['_free-product-addon'] === 'true');
    const doesCartAlreadyHaveThreeMonthsFree = cart.items.some(
      (item) => item.variant_id === parseFloat(cartDrawer.dataset.freeProductAddonVariantId),
    );

    const variantId = cartDrawer.dataset.freeProductAddonVariantId;
    const quantity = 1;

    if (doesAnyCartItemHaveTag && !doesCartAlreadyHaveThreeMonthsFree) {
      console.log('[CartItems] Adding three months free product to cart');
      const cartData = await this.addThreeMonthsFreeToCart(variantId, quantity);
      if (cartData) {
        // The UI has already been updated in addThreeMonthsFreeToCart
        // Just publish the event for other components that might need it
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          productVariantId: variantId,
          sourceId: this.instanceId,
          cartData: cartData.cartData,
        });
      }
    } else if (!doesAnyCartItemHaveTag && doesCartAlreadyHaveThreeMonthsFree) {
      console.log('[CartItems] Removing three months free product from cart');
      const freeMonthsIndex = cart.items.findIndex(
        (item) => item.variant_id === parseFloat(cartDrawer.dataset.freeProductAddonVariantId),
      );

      if (freeMonthsIndex !== -1) {
        this.updateQuantity(
          freeMonthsIndex + 1,
          0,
          'updates[]',
          parseFloat(cartDrawer.dataset.freeProductAddonVariantId),
        );
      }
    }
    return doesAnyCartItemHaveTag;
  }

  async addThreeMonthsFreeToCart(variantId, quantity) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseData = await response.json();

      // Get fresh sections data
      const sectionsResponse = await fetch(
        window.Shopify.routes.root +
          '?sections=' +
          this.getSectionsToRender()
            .map((section) => section.id)
            .join(','),
      );
      const sectionsData = await sectionsResponse.json();

      // Get updated cart state
      const cartData = await this.getCartState();

      // Update UI with new data
      this.updateSectionsWithMarkup(sectionsData);
      this.refreshCartDrawer();
      this.updateCartUI(cartData);

      return {
        sections: sectionsData,
        cartData: cartData,
        id: variantId.toString(),
      };
    } catch (error) {
      console.error('Error adding to cart:', error);
      return null;
    }
  }

  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const response = await fetch(`${routes.cart_url}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newItems = doc.querySelector('.cart-drawer__items');
    const currentItems = document.querySelector('.cart-drawer__items');
    if (newItems && currentItems) {
      currentItems.innerHTML = newItems.innerHTML;
      this.setupQuantitySelectors();
    }

    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = document.querySelector('.cart-drawer__footer');
    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }
  }

  async getCartState() {
    const response = await fetch(`${routes.cart_url}.js`);
    return response.json();
  }

  updateCartUI(cart, forceShowUI = false) {
    console.log('[CartItems] Updating cart UI:', {
      cart,
      cartItemCount: cart?.items?.length,
      currentUrl: window?.location?.href,
    });

    const cartDrawer = document.querySelector('cart-drawer');
    const button = document.querySelector('#CartDrawer-Checkout');
    const cartCounts = document.querySelectorAll('.cart-count-bubble span');

    // Only update cart count if it hasn't been updated by sections
    // Check if the cart count is already correct
    const firstCartCount = cartCounts[0];
    if (firstCartCount && firstCartCount.textContent !== cart.item_count.toString()) {
      console.log('[CartItems] Updating cart count from', firstCartCount.textContent, 'to', cart.item_count);
      cartCounts.forEach((cartCount) => {
        cartCount.textContent = cart.item_count;
        cartCount.classList.toggle('hidden', cart.item_count === 0);
      });
    } else {
      console.log('[CartItems] Cart count already up to date:', cart.item_count);
    }

    if (cartDrawer) {
      if (forceShowUI || cart.items.length > 0 || cart.variant_id) {
        button.disabled = false;
        cartDrawer.classList.remove('is-empty');
      } else {
        button.disabled = true;
        cartDrawer.classList.add('is-empty');
      }
    }
  }

  hideCartUI() {
    const cartDrawer = document.querySelector('cart-drawer');
    const button = document.querySelector('#CartDrawer-Checkout');

    button.disabled = true;
    cartDrawer.classList.add('is-empty');
  }

  handleError(line, errors) {
    const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
    if (quantityElement) {
      quantityElement.value = quantityElement.querySelector('input')?.value || quantityElement.dataset.maxQuantity;
    }
    this.updateLiveRegions(line, errors);
  }

  enableLoading(line) {
    const mainCartItems = document.querySelector('.cart-drawer__items');
    mainCartItems?.classList.add('cart__items--disabled');

    const spinner = this.querySelector(`#CartDrawer-Item-${line} .loading__spinner`);
    spinner?.classList.remove('hidden');

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.querySelector('.cart-drawer__items');
    mainCartItems?.classList.remove('cart__items--disabled');

    const spinner = this.querySelector(`#CartDrawer-Item-${line} .loading__spinner`);
    spinner?.classList.add('hidden');
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sectionId = this.dataset.sectionId;
    return [
      {
        id: sectionId,
        selector: '.drawer__inner',
        section: 'cart-drawer-items,.cart-drawer__drawer-footer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
